import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 2200);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Reset failed. Please request a new link.");
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div data-testid="reset-page" className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-4" strokeWidth={1.25} />
          <h1 className="font-serif-display text-3xl mb-3">Invalid reset link</h1>
          <p className="text-[var(--ink-soft)] mb-8">This link is missing its security token. Please request a fresh one.</p>
          <Link to="/forgot-password" data-testid="reset-request-new"
            className="inline-block bg-[var(--terracotta)] text-[var(--ink)] px-6 py-2.5 rounded-full text-sm hover:bg-[var(--terracotta-dark)]">
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reset-page" className="min-h-[80vh] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={LOGO_URL} alt="50 States Sober" className="w-16 h-16 rounded-full object-cover ring-1 ring-[var(--line)]" />
        </div>

        {done ? (
          <div data-testid="reset-done" className="text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-[var(--forest)] mb-4" strokeWidth={1.25} />
            <h1 className="font-serif-display text-3xl mb-3">Password updated</h1>
            <p className="text-[var(--ink-soft)] mb-6">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-serif-display text-4xl text-center mb-2">Set a new password</h1>
            <p className="text-[var(--ink-soft)] text-center mb-10">
              Choose something you&apos;ll remember. At least 6 characters.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <input
                data-testid="reset-password"
                type="password" required minLength={6}
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="New password"
                className="w-full px-4 py-3 border border-[var(--line)] rounded-sm text-sm focus:outline-none focus:border-[var(--terracotta)] bg-[var(--bg)]"
              />
              <input
                data-testid="reset-confirm"
                type="password" required minLength={6}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                className="w-full px-4 py-3 border border-[var(--line)] rounded-sm text-sm focus:outline-none focus:border-[var(--terracotta)] bg-[var(--bg)]"
              />
              {err && <div data-testid="reset-error" className="text-sm text-red-600">{err}</div>}
              <button data-testid="reset-submit" disabled={loading}
                className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-3 rounded-sm text-sm hover:bg-[var(--terracotta-dark)] disabled:opacity-50">
                {loading ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
